/**
 * Ported from deexoptions-mobile-app/utils/activity/displayStatus.ts.
 *
 * PWA DIVERGENCE — read before changing `maskFailed` at any callsite.
 *
 * Mobile masks failed → pending for **payouts only**. The PWA has always masked
 * more broadly: `shouldMaskFailedAsPending` in ../transactionStatus.ts hides
 * failure for every non-utility, non-giftcard transaction, withdrawals included,
 * and the receipt modal still uses that rule.
 *
 * The PWA rule is the one we keep. If a list row used mobile's narrower rule
 * while the receipt kept this one, a failed crypto withdrawal would read
 * "Failed" in the list and "PENDING" in the receipt opened from that same row.
 *
 * So: callers pass `maskFailed: maskFailedForType(item.type)` — not
 * `type === "payout"` as mobile does.
 */
import type { ActivityType } from "./filters";

export type DisplayStatus =
  | "pending"
  | "completed"
  | "success"
  | "failed"
  | "in progress";

/**
 * The PWA's masking rule, expressed over normalized types. Mirrors
 * `shouldMaskFailedAsPending`: bill payments (mobile's `utility`) and gift cards
 * show their true status; everything else hides failure.
 */
export const maskFailedForType = (type: ActivityType) =>
  type !== "billpayment" && type !== "giftcard";

export const toDisplayStatus = (
  status?: string,
  options?: { maskFailed?: boolean }
): DisplayStatus => {
  const normalized = status?.trim().toLowerCase();

  if (!normalized) return "completed";

  if (normalized === "failed") {
    return options?.maskFailed ? "pending" : "failed";
  }

  if (normalized === "processing" || normalized === "in progress")
    return "in progress";
  if (normalized === "success") return "success";
  if (normalized === "completed") return "completed";
  if (normalized === "pending") return "pending";

  // Unknown/unhandled backend states are surfaced as pending rather than
  // rendered raw — an unstyled, unexpected label is worse than "pending".
  return "pending";
};

/** True when the row should read as pending to the user. */
export const isDisplayPending = (
  status?: string,
  options?: { maskFailed?: boolean }
) => toDisplayStatus(status, options) === "pending";

/**
 * True when a transaction has settled successfully — the only state allowed to
 * produce a shareable receipt.
 *
 * A receipt is a proof-of-completion artifact: it carries Deex branding, it is
 * built to leave the platform, and once it's an image it no longer has a status
 * attached. A "receipt" for a failed withdrawal is something a user could
 * present as proof of payment, so the gate is deliberately a whitelist rather
 * than a `!== "failed"` check — that way genuinely unknown backend states (HOLD
 * is a live one, unhandled by `toDisplayStatus`) fail closed instead of open.
 *
 * Transactions with no status at all stay shareable: `toDisplayStatus` maps
 * those to "completed" by design, because crypto deposits and some legacy bill
 * rows simply don't carry the field.
 */
export const canShareReceipt = (
  status?: string,
  options?: { maskFailed?: boolean }
) => {
  const display = toDisplayStatus(status, options);
  return display === "completed" || display === "success";
};
